// ============================================================================
// PerformanceContext — fonte ÚNICA de verdade pra dados mesclados Avec + Local
// ============================================================================
// Antes desta refatoração, cada tela (Dashboard, Ranking, Reports, Conquistas
// etc) chamava `mergePerformanceWithAvec` independentemente. O cálculo é
// pesado (matching nome/CPF/apelido por prof × mês × unidade), e era refeito
// 7× em paralelo a cada sync da Avec — mesmas entradas, mesmo resultado.
//
// Centralizando aqui:
//   - O merge roda 1× por mudança de inputs (cache/snapshots/locais)
//   - Todas as telas leem o MESMO objeto, garantindo consistência absoluta
//     (sem janela de "1 frame divergente" entre componentes)
//   - Trocas de tela ficam instantâneas (nada pra recalcular)
//
// Sem mudança no comportamento de rede: a API Avec continua sendo chamada
// SOMENTE pelo AvecSyncContext (polling de 60s). Este context só transforma
// dados que já estão em memória.
// ============================================================================

import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useData } from './DataContext.jsx';
import { useAvecSync } from './AvecSyncContext.jsx';
import {
  mergePerformanceWithAvec,
  mergeUnitPerformanceWithAvec,
  discoverProfessionalCpfs,
} from '../utils/avecPerformanceMerger.js';
import { sensivelSemMarca } from '../utils/cadastroSensivel.js';
import { chaveAplicadaDoCofre } from '../utils/storage.js';
import { CADASTRO_SEED_TRAVADO } from '../config/featureFlags.js';

const PerformanceContext = createContext(null);

export function PerformanceProvider({ children }) {
  const {
    professionals,
    performance: localPerformance,
    unitPerformance: localUnitPerformance,
    services,
    packages,
    updateProfessional,
  } = useData();
  const avec = useAvecSync();

  // ─── Auto-cadastro de CPFs vindos da Avec ──────────────────────────────────
  // Toda vez que a Avec sincroniza, varre `avecListCompleta` e cadastra o CPF
  // no prof FK que ainda não tem. Depois disso o matching futuro usa CPF
  // (estratégia 1, mais confiável que nome/apelido), evitando casos onde
  // "Day"/"Gringo" não casavam com "Daiane Fabiano dos Santos" e ficavam zerados.
  //
  // CRÍTICO: usa `silent: true` no update pra NÃO carimbar updatedAt.
  // Carimbar fazia o discovery virar um vetor de conflito multi-device — outro
  // device com cache antigo descobria os mesmos CPFs e empurrava versão velha
  // com timestamp NOVO, sobrescrevendo edições reais (role, active=false).
  //
  // Dedup também persiste em localStorage pra não re-descobrir após reload.
  //
  // CADASTRO_SEED_TRAVADO (28/10/2026, caso Renato): só roda depois que o
  // fk_professionals foi APLICADO do cofre nesta sessão (não basta o cloudLoaded,
  // que vira true no tempo esgotado, no "não mudou" e com a chave suja) e manda
  // só o cpf (campos: ['cpf']). Em 28/10 ela rodou na montagem sobre a cópia do
  // seed e subiu a lista inteira.
  const lastSavedCpfsRef = useRef(null);
  useEffect(() => {
    if (!professionals?.length) return;
    if (CADASTRO_SEED_TRAVADO && !chaveAplicadaDoCofre('fk_professionals')) return;
    // Inicializa dedup do localStorage (sobrevive reload)
    if (!lastSavedCpfsRef.current) {
      try {
        const raw = localStorage.getItem('_fk_discovered_cpfs') || '[]';
        lastSavedCpfsRef.current = new Set(JSON.parse(raw));
      } catch {
        lastSavedCpfsRef.current = new Set();
      }
    }
    // CPF é campo SENSÍVEL do cadastro (06/10/2026): registro que veio com a
    // marca do servidor (oculto/só leitura) não é desta sessão editar — o
    // servidor ignoraria; nem tenta (e não põe CPF na cópia local). E o
    // dedup local não guarda CPF de colega fora do escopo.
    const editavel = new Set(professionals.filter(sensivelSemMarca).map(p => p.id));
    let changed = false;
    for (const k of [...lastSavedCpfsRef.current]) {
      if (!editavel.has(String(k).split('|')[0])) { lastSavedCpfsRef.current.delete(k); changed = true; }
    }
    const discoveries = discoverProfessionalCpfs(avec?.cache, avec?.snapshots, professionals);
    for (const { profId, cpf } of discoveries) {
      if (!editavel.has(profId)) continue;
      const key = `${profId}|${cpf}`;
      if (lastSavedCpfsRef.current.has(key)) continue;
      lastSavedCpfsRef.current.add(key);
      changed = true;
      updateProfessional({ id: profId, cpf }, CADASTRO_SEED_TRAVADO ? { silent: true, campos: ['cpf'] } : { silent: true });
    }
    if (changed) {
      try {
        localStorage.setItem('_fk_discovered_cpfs',
          JSON.stringify([...lastSavedCpfsRef.current]));
      } catch {}
    }
  }, [avec?.cache, avec?.snapshots, professionals, updateProfessional]);

  // Merge prof-level (records por professionalId × ano × mês).
  // Usado por: Dashboards, Ranking, Reports, Conquistas, Professionals,
  // ProfChallengesAdminProgress.
  const performance = useMemo(
    () => mergePerformanceWithAvec(
      localPerformance,
      avec.cache,
      avec.snapshots,
      professionals,
      services,
      packages,
    ),
    [localPerformance, avec.cache, avec.snapshots, professionals, services, packages]
  );

  // Merge unit-level (records por unitId × ano × mês).
  // Usado por: Dashboard admin, Reports, Dashboard prof.
  const unitPerformance = useMemo(
    () => mergeUnitPerformanceWithAvec(
      localUnitPerformance,
      avec.cache,
      avec.snapshots,
      services,
    ),
    [localUnitPerformance, avec.cache, avec.snapshots, services]
  );

  const value = useMemo(
    () => ({ performance, unitPerformance }),
    [performance, unitPerformance]
  );

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
}

export function usePerformance() {
  const ctx = useContext(PerformanceContext);
  if (!ctx) throw new Error('usePerformance deve ser usado dentro de <PerformanceProvider>');
  return ctx;
}
